import { useCallback, useEffect, useState } from "react";
import { FileText, LockKeyhole, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import CodePad from "@/components/vault/CodePad";
import { ACCEPT, checkFile, uploadFile } from "@/lib/upload13";

const CATS = ["Medical", "Insurance", "Identity", "School", "Other"];
const SENSITIVE = ["Medical", "Insurance", "Identity"];

function Attach({ hubId, member, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("Medical");
  const [tok, setTok] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/organizations/${hubId}`;
  const needCode = SENSITIVE.includes(cat) && !tok;
  const unlock = async (code) => {
    try { const { data } = await api.post(`${base}/vault/unlock`, { code }); setTok(data.token); setErr(null); } catch (e) { setErr(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const uid = await uploadFile(`${base}/emergency`, file);
      await api.post(`${base}/emergency/cards/${member.key}/documents`, { upload_id: uid, title, category: cat }, { headers: tok ? { "X-Vault-Token": tok } : {} });
      toast.success("Filed in Documents"); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={`Attach document for ${member.name}`} description="Filed once in Documents, belonging to this person." testid="em-attach-dialog">
      <div className="space-y-3">
        <Field label="Title (optional)"><input className="q-input min-h-[44px] w-full" maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Allergy action plan" data-testid="em-attach-title" /></Field>
        <Field label="Category"><select className="q-input min-h-[44px] w-full" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="em-attach-category">{CATS.map((c) => <option key={c} value={c}>{c}{SENSITIVE.includes(c) ? " (sensitive)" : ""}</option>)}</select></Field>
        <label className="q-btn q-btn-secondary min-h-[44px] w-full cursor-pointer" data-testid="em-attach-file">
          <Paperclip className="h-4 w-4" /> {file ? file.name : "Choose file or take photo"}
          <input type="file" accept={ACCEPT} className="hidden" onChange={(e) => { const x = e.target.files[0]; e.target.value = ""; const bad = x && checkFile(x); if (bad) toast.error(bad); else setFile(x || null); }} data-testid="em-attach-input" />
        </label>
        {needCode && <CodePad title="Enter your vault code" hint="Sensitive documents need your vault code." error={err} busy={false} onSubmit={unlock} />}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy || !file || needCode} onClick={save} data-testid="em-attach-save">{busy ? "Saving…" : "Attach"}</button>
      </div>
    </FormDialog>
  );
}

export default function CardDocs({ hubId, member, locked }) {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const id = member.name.split(" ")[0];
  const load = useCallback(() => api.get(`/organizations/${hubId}/emergency/cards/${member.key}/documents`).then(({ data }) => setDocs(data.documents)).catch(() => setDocs([])), [hubId, member.key]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-4 border-t border-q-border pt-3" data-testid={`em-docs-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-[10px] font-black uppercase tracking-[0.14em] text-q-faint">Documents</p>
        {member.can_edit && <button className="q-btn q-btn-ghost min-h-[44px]" disabled={locked} onClick={() => setOpen(true)} data-testid={`em-attach-btn-${id}`}><Paperclip className="h-4 w-4" /> Attach document</button>}
      </div>
      {docs?.map((x) => (
        <a key={x.id} href={`/h/${hubId}/family/documents`} className="flex min-h-[44px] items-center gap-2 text-sm text-q-text hover:underline" data-testid={`em-doc-${x.id}`}>
          {x.sensitive ? <LockKeyhole className="h-4 w-4 text-q-muted" /> : <FileText className="h-4 w-4 text-q-muted" />}
          {x.sensitive ? `Sensitive document · ${x.category}` : `${x.title} · ${x.category}`}
        </a>
      ))}
      {open && <Attach hubId={hubId} member={member} onClose={() => setOpen(false)} onDone={load} />}
    </div>
  );
}
